/**
 * `Bill.breakdown` as arqam's `app_price` builds it (arqam migration 045):
 * every amount is computed there. The app only reads and shows it: these
 * helpers never compute money.
 */

export type BreakdownTier = {
  tierStart: number;
  tierEnd: number | null;
  rate: number;
  description: string | null;
  consumptionInTier: number;
  tierPrice: number;
};

export type BreakdownFee = {
  name: string;
  description: string | null;
  feeType: 'FIXED' | 'PERCENTAGE';
  amount: number;
  calculatedAmount: number;
  applied: boolean;
  onlyWhenNoConsumption: boolean;
};

export type Breakdown = {
  consumption: number;
  consumptionPrice: number;
  totalFees: number;
  shartPrice: number;
  totalBill: number;
  tiers: BreakdownTier[];
  fees: BreakdownFee[];
  shart: {
    members: string[] | null;
    memberCount: number;
    unitPrice: number;
    totalPrice: number;
  };
};

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** The breakdown if it has the expected shape, else null (shown as missing). */
export function readBreakdown(value: unknown): Breakdown | null {
  if (!isObject(value)) return null;
  if (!Array.isArray(value.tiers) || !Array.isArray(value.fees)) return null;
  if (!isObject(value.shart) || typeof value.totalBill !== 'number')
    return null;
  return value as unknown as Breakdown;
}

/** The tiers this bill's consumption actually used. */
export const usedTiers = (b: Breakdown) =>
  b.tiers.filter((t) => t.consumptionInTier > 0);

/** The fees charged on this bill (e.g. a freeze fee only applies at 0 m³). */
export const appliedFees = (b: Breakdown) => b.fees.filter((f) => f.applied);

/** «من 1 إلى 30 م³» / «من 31 م³ فما فوق» (a tier starting at 0 shows from 1). */
export function tierRange(t: Pick<BreakdownTier, 'tierStart' | 'tierEnd'>) {
  const from = Math.max(t.tierStart, 1);
  return t.tierEnd === null
    ? `من ${from} م³ فما فوق`
    : `من ${from} إلى ${t.tierEnd} م³`;
}
